import { useRef, useState } from 'react';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { optionListHasUnsavedChanges, visibleOptionRows } from '../lib/optionListView.ts';
import { normalizeStringList } from '../lib/normalizeStringList.ts';

type Row = { id: number; value: string };

// The artboard restyles these controls (8px radius, accent focus ring) rather
// than reusing the app's default field chrome, so the classes live here instead
// of coming from @l2u/ui's TextInput. Focus uses the brand token, so a retheme
// carries through.
const FIELD_BASE =
  'w-full min-w-0 rounded-lg border bg-white text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--brand-accent)] focus:ring-[3px] focus:ring-[color-mix(in_srgb,var(--brand-accent)_10%,transparent)]';

const ACCENT_OUTLINE_BTN =
  'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[13px] font-semibold text-[var(--brand-accent)] transition hover:border-[color-mix(in_srgb,var(--brand-accent)_35%,white)] hover:bg-[var(--brand-active-bg)]';

/**
 * The Settings option-list editor: one text input per entry, plus the card
 * chrome the design calls for — entry count, unsaved-changes badge, search,
 * add-at-top, an empty state that offers to add what was searched for, and a
 * "showing N of M" footer while a filter is active.
 *
 * Deliberately separate from the shared StringListEditor, which stays a plain
 * row editor for QuestionBuilder's answer options and the company panels. The
 * two look nothing alike, and expressing this card through props on the shared
 * component would have left it serving two unrelated designs.
 *
 * Rows are seeded once from `value`; later `value` changes are ignored, because
 * the raw typed rows are the source of truth while the admin types (trailing
 * spaces and a blank new row have to survive keystrokes). Re-seeding is a
 * remount — OptionListPanel bumps a `key` on save, which is what makes the list
 * alphabetise on Save and never mid-edit.
 *
 * The search text is owned by the caller, above that `key`, so a save re-seeds
 * the rows without throwing away the admin's query.
 */
export function OptionListEditor({
  value,
  savedValue,
  onChange,
  onSave,
  saving,
  title,
  hint,
  itemLabel,
  itemLabelPlural,
  addLabel,
  placeholder,
  search,
  onSearchChange,
  maxLength = 120,
}: {
  value: string[];
  /** The stored list, for the unsaved-changes badge. */
  savedValue: string[];
  onChange: (next: string[]) => void;
  onSave: () => void;
  saving: boolean;
  title: string;
  hint: string;
  itemLabel: string;
  itemLabelPlural: string;
  addLabel: string;
  placeholder: string;
  search: string;
  onSearchChange: (next: string) => void;
  maxLength?: number;
}) {
  // Stable per-row ids so React keys survive insertion/deletion (index keys
  // would let a deleted middle row steal a sibling's focus/cursor).
  const nextId = useRef(0);
  const makeRow = (v: string): Row => ({ id: nextId.current++, value: v });
  const [rows, setRows] = useState<Row[]>(() => (value.length === 0 ? [''] : value).map(makeRow));

  // Rows added since the search box was last touched. A row is empty the moment
  // it is added, so it matches no non-blank query — without this, pressing Add
  // while a filter was active would look like it did nothing.
  const [addedIds, setAddedIds] = useState<ReadonlySet<number>>(() => new Set());

  // Keep at least one (possibly empty) row so the editor never collapses to
  // just the add button. Empty rows normalize away, so persistence is
  // unaffected.
  function update(next: Row[]) {
    const safe = next.length === 0 ? [makeRow('')] : next;
    setRows(safe);
    onChange(normalizeStringList(safe.map((r) => r.value)));
  }

  function addRow(initialValue: string) {
    const row = makeRow(initialValue);
    setAddedIds((prev) => new Set(prev).add(row.id));
    update([row, ...rows]);
  }

  function setQuery(next: string) {
    onSearchChange(next);
    setAddedIds(new Set());
  }

  // Row numbering follows the full list, not the filtered view, so a row's
  // accessible name does not change as the admin types in the search box.
  const positions = new Map(rows.map((row, i) => [row.id, i + 1]));
  const shown = visibleOptionRows(rows, search, addedIds);

  const query = search.trim();
  const filtering = query.length > 0;
  const countOf = (list: readonly Row[]) => list.filter((r) => r.value.trim().length > 0).length;
  const total = countOf(rows);
  const shownCount = countOf(shown);
  // While saving, the edited copy is already sorted and the fetched copy is not,
  // so the badge would flicker on every save if it were not suppressed here.
  const dirty = !saving && optionListHasUnsavedChanges(value, savedValue);

  return (
    // Cancels the tab panel's padding so the header rule and the toolbar strip
    // reach the card's edges, as the design has them.
    <div className="-m-6">
      <header className="flex items-start gap-6 border-b border-slate-100 px-7 pb-5 pt-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-[var(--brand-accent)]"
              style={{ backgroundColor: 'var(--brand-active-bg)' }}
            >
              {total}
            </span>
          </div>
          <p className="mt-2 max-w-[620px] text-[13px] leading-relaxed text-slate-500">{hint}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {dirty && (
            <span className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700">
              Unsaved changes
            </span>
          )}
          <Btn onClick={onSave} loading={saving}>
            Save
          </Btn>
        </div>
      </header>

      <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-7 py-4">
        <div className="relative min-w-0 flex-1 sm:max-w-[420px]">
          <svg
            aria-hidden="true"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${itemLabelPlural}`}
            aria-label={`Search ${title}`}
            className={`${FIELD_BASE} border-slate-300 py-2.5 pl-[30px] pr-9`}
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-1.5 py-1 text-[15px] leading-none text-slate-400 transition hover:text-slate-900"
            >
              ×
            </button>
          )}
        </div>
        <div className="flex-1" />
        <span className="hidden text-xs text-slate-400 sm:inline">Sorts A–Z on save</span>
        <button type="button" onClick={() => addRow('')} className={ACCENT_OUTLINE_BTN}>
          <span aria-hidden="true">+</span>
          {addLabel}
        </button>
      </div>

      <div className="px-7 pb-5 pt-2">
        {shown.map((row) => (
          <div key={row.id} className="flex items-center gap-2.5 py-[5px]">
            <input
              type="text"
              value={row.value}
              placeholder={placeholder}
              maxLength={maxLength}
              aria-label={`${itemLabel} ${positions.get(row.id)}`}
              onChange={(e) =>
                update(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))
              }
              className={`${FIELD_BASE} border-slate-200 px-3.5 py-2.5 tracking-[0.02em] hover:border-slate-300`}
            />
            <button
              type="button"
              title={`Delete ${itemLabel}`}
              aria-label={`Remove ${row.value.trim() || `${itemLabel} ${positions.get(row.id)}`}`}
              onClick={() => update(rows.filter((r) => r.id !== row.id))}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-transparent text-slate-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 7h16" />
                <path d="M10 4h4" />
                <path d="M6 7l1 13h10l1-13" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </button>
          </div>
        ))}

        {filtering && shown.length === 0 && (
          <div className="px-0 pb-8 pt-10 text-center">
            <p className="m-0 text-sm text-slate-500">
              No {itemLabelPlural} match “{query}”.
            </p>
            <button
              type="button"
              onClick={() => addRow(query)}
              className={`mt-3.5 ${ACCENT_OUTLINE_BTN} border-[color-mix(in_srgb,var(--brand-accent)_35%,white)]`}
              style={{ backgroundColor: 'var(--brand-active-bg)' }}
            >
              <span aria-hidden="true">+</span>
              Add “{query}” as a {itemLabel}
            </button>
          </div>
        )}

        {filtering && shown.length > 0 && (
          <p className="mt-3.5 text-xs text-slate-400">
            Showing {shownCount} of {total} {itemLabelPlural}. Clear the search to edit the full
            list.
          </p>
        )}
      </div>
    </div>
  );
}
